/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
// import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext"; // Custom Code
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
// import React, { ChangeEvent, useEffect, useState } from "react"; // Custom Code
import { connect } from "react-redux";
import { css } from "styled-components";

/* Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import useExpandProps from "@store/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { useCanAddToCart, useHasEnoughInventory } from "@store/Framework/Store/Data/Products/ProductsHooks";
import useOnDemandInventory from "@store/Framework/Common/Hooks/useOnDemandInventory";
import useLiquidationQuantityMessage from "@store/Library/Hooks/useLiquidationQuantityMessage";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";

export interface ProductQuantityOrderedProps extends Omit<OwnProps, keyof TextFieldPresentationProps> {}

export const useShowProductQuantityOrdered = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);
    const canAddToCart = useCanAddToCart(product, productInfo, configurationCompleted, variantSelectionCompleted);

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart);
};
/* Custom Code - End */

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    // Custom Code
    overridedStyles?: TextFieldPresentationProps;
    rightContent?: React.ReactNode;
    multipleQtyMessage?: React.ReactNode;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    sizeVariant: "small",
    cssOverrides: {
        descriptionWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.descriptionWrapper}
        `,
        formField: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
            width: auto;
        `,
        formInputWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formInputWrapper}
        `,
        inputSelect: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.inputSelect}
            width: ${pxToRem(55)};
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, isDiscontinued, detail },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory, inventory, pricing },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    overridedStyles, // Custom Code
    onBlur = () => {}, // Custom Code
    rightContent, // Custom Code
    multipleQtyMessage, // Custom Code
    ...otherProps
}: Props) => {
    const styles = useExpandProps(productQuantityOrderedStyles, extendedStyles, overridedStyles); // Custom Code
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const onDemandInventory = useOnDemandInventory(productId);

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if (!useShowProductQuantityOrdered(configurationCompleted, variantSelectionCompleted) /* Custom Code */) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    const enteredQty = Number(productQty);
    const inventoryToUse = onDemandInventory || inventory;
    const { isLiquidation, availableQty } = useLiquidationQuantityMessage({
        liquidationValue: detail?.name,
        requestedQty: enteredQty,
        availableQty: inventoryToUse?.qtyOnHand,
    });

    return (
        <div style={{ width: '100%' }}>
            {(labelOverride !== undefined && labelOverride !== null) || rightContent ? (
                <span style={{ display: 'block', marginBottom: '8px' }}>
                    {labelOverride ?? translate("QTY")}
                    {multipleQtyMessage && (
                        <span style={{ color: '#00305e' }}> ({multipleQtyMessage})</span>
                    )}
                </span>
            ) : null}
            <div style={{ display: 'flex', gap: pxToRem(10), alignItems: 'flex-start', width: '100%' }}>
                <TextField
                    label=""
                    value={productQty}
                    type="number"
                    min={0}
                    max={999999}
                    data-test-selector="product_qtyOrdered"
                    {...otherProps}
                    onKeyPress={keyPressHandler}
                    onChange={qtyChangeHandler}
                    onBlur={onBlur}
                    {...styles}
                />
                {rightContent && <div style={{ flex: 1 }}>{rightContent}</div>}
            </div>
            {isLiquidation && (
                <span style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: '#52616f' }}>
                    {translate("Available quantity")}: {availableQty}
                </span>
            )}
        </div>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
